import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, NavLink } from "react-router-dom";
import { AirowLogo, AirowIconMark } from "@/components/AirowLogo";
import { useSidebar } from "@/components/ui/sidebar";


import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Bell, LogOut, User, Sun, Moon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";

export const PortalHeader = () => {
  const { user, signOut } = useAuth();
  const { i18n, t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const navigate = useNavigate();

  const [language, setLanguage] = useState<"en-CA" | "fr-CA">("en-CA");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) return;

      const { data } = await supabase
        .from("profiles")
        .select("language, avatar_url")
        .eq("id", user.id)
        .maybeSingle();

      if (data?.language) {
        const lang = data.language as "en-CA" | "fr-CA";
        setLanguage(lang);
        i18n.changeLanguage(lang);
      }
      
      if (data?.avatar_url) {
        setAvatarUrl(data.avatar_url);
      }
    };

    loadUserProfile();
  }, [user]);

  const switchLanguage = async (newLang: "en-CA" | "fr-CA") => {
    if (newLang === language) return;

    try {
      localStorage.setItem("i18nextLng", newLang);
    } catch {}

    setLanguage(newLang);
    await i18n.changeLanguage(newLang);

    if (user) {
      await supabase
        .from("profiles")
        .update({ language: newLang })
        .eq("id", user.id);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const getInitials = () => {
    if (!user?.email) return "U";
    return user.email.substring(0, 2).toUpperCase();
  };

  const isEn = language === "en-CA";

  return (
    <header className="h-16 shrink-0 border-b border-border/50 bg-card/80 backdrop-blur-sm flex items-center justify-between pr-3 sm:pr-6 sticky top-0 z-20 w-full">
      <div className="flex items-center gap-3">
        <NavLink
          to="/"
          className={`flex items-center shrink-0 text-foreground ${
            collapsed ? "pl-4" : "pl-3 sm:pl-6"
          }`}
        >
          {collapsed ? (
            <AirowIconMark className="h-7 w-7" />
          ) : (
            <AirowLogo className="h-7 w-auto" />
          )}
        </NavLink>
      </div>



      <div className="flex items-center gap-3">
        {/* Dark/Light toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-9 w-9 rounded-full"
        >
          {theme === "dark" ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
          <span className="sr-only">Toggle theme</span>
        </Button>

        {/* Language toggle - EN | FR pill */}
        <div className="flex items-center rounded-full bg-muted p-0.5 gap-0.5">
          <button
            onClick={() => switchLanguage("en-CA")}
            className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${
              isEn
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            EN
          </button>
          <button
            onClick={() => switchLanguage("fr-CA")}
            className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-colors ${
              !isEn
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            FR
          </button>
        </div>

        <Button variant="ghost" size="icon">
          <Bell className="h-5 w-5" />
          <span className="sr-only">Notifications</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-10 w-10 rounded-full">
              <Avatar>
                <AvatarImage src={avatarUrl || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground font-medium">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">My Account</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <User className="mr-2 h-4 w-4" />
              {t('nav.profileSettings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              {t('header.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
